// client/src/api/http.js
import { logger } from "@/utils/logger";
import { withRetry } from "@/shared/utils/retry";

import { getAuthToken } from "./auth.js";

const EDGE_ORIGIN = import.meta.env.VITE_EDGE_ORIGIN || "https://api.yebo-ai.com";

function normalizeApiBase(origin) {
  const raw = String(origin || "").trim().replace(/\/+$/, "");
  if (!raw) return "";
  return /\/api$/i.test(raw) ? raw : `${raw}/api`;
}

export const BASE = normalizeApiBase(EDGE_ORIGIN);

export const DEFAULT_API_CONFIG = {
  version: "2021-07-28",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

/**
 * Global log suppression:
 * - By default, suppress 500 and 502 console logs (errors still throw).
 * - Override with REACT_APP_SUPPRESS_STATUSES (e.g., "500,502,503").
 */
function parseSuppressedStatuses(envVal) {
  const raw = (envVal ?? "500,502").split(",");
  const nums = raw
    .map((s) => parseInt(String(s).trim(), 10))
    .filter((n) => Number.isFinite(n) && n >= 100 && n <= 599);
  return new Set(nums);
}
const SUPPRESS_STATUSES = parseSuppressedStatuses(import.meta.env.VITE_SUPPRESS_STATUSES);

// ---- Resilience defaults ----
const DEFAULT_TIMEOUT_MS = 10000; // abort each attempt after 10s
const DEFAULT_ATTEMPTS = 3;       // total attempts per request (1 initial + 2 retries)
const DEFAULT_BASE_DELAY = 250;   // ms, exponential backoff base

// ---- GET cache defaults ----
const DEFAULT_CACHE_TTL_MS = 30_000;
const CACHE_TTL_BY_PATH = [
  { matcher: /\/calendars\/events\/blocked-slots/i, ttlMs: 15_000 },
  { matcher: /\/calendars\/blocked-slots/i, ttlMs: 15_000 },
  { matcher: /\/calendars\/events/i, ttlMs: 45_000 },
];

// In-flight and response cache are module-level to dedupe requests across callers.
const inFlight = new Map();
const responseCache = new Map();

/** Clear cached responses whose keys match `pattern` (regex or string). Omit to clear all. */
export function clearResponseCache(pattern) {
  if (!pattern) { responseCache.clear(); return; }
  const re = typeof pattern === "string" ? new RegExp(pattern, "i") : pattern;
  for (const key of responseCache.keys()) {
    if (re.test(key)) responseCache.delete(key);
  }
}

/**
 * Inject default locationId from env if not provided.
 */
function ensureLocationId(params = {}) {
  const loc = import.meta.env.VITE_LOCATION_ID;
  if (loc && !params.locationId) {
    return { ...params, locationId: String(loc) };
  }
  return params;
}

/**
 * Append query params to URL (skips undefined/null/empty string).
 */
function withQuery(url, params) {
  const fullParams = ensureLocationId(params);
  if (!fullParams || !Object.keys(fullParams).length) return url;

  let paramsToUse = fullParams;
  if (Object.prototype.hasOwnProperty.call(fullParams, "locationId")) {
    let hasLocationId = false;
    try {
      const parsed = new URL(url, "http://localhost");
      hasLocationId = parsed.searchParams.has("locationId");
    } catch {
      hasLocationId = url.includes("locationId=");
    }
    if (hasLocationId) {
      paramsToUse = { ...fullParams };
      delete paramsToUse.locationId;
    }
  }

  const usp = new URLSearchParams();
  Object.entries(paramsToUse).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    usp.append(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `${url}?${qs}` : url;
}

function normalizeHeaderIdentity(headers) {
  if (!headers) return "";

  if (typeof headers.get === "function") {
    const auth = headers.get("authorization") || headers.get("Authorization") || "";
    const version = headers.get("version") || headers.get("Version") || "";
    return `auth:${auth}|version:${version}`;
  }

  const auth = headers.Authorization || headers.authorization || "";
  const version = headers.Version || headers.version || "";
  return `auth:${auth}|version:${version}`;
}

function buildSortedQueryString(url, params) {
  const parsed = new URL(url, "http://localhost");
  const merged = [];

  for (const [k, v] of parsed.searchParams.entries()) {
    if (v !== "") merged.push([k, v]);
  }

  const fullParams = ensureLocationId(params);
  if (fullParams) {
    Object.entries(fullParams).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") return;
      merged.push([k, String(v)]);
    });
  }

  const deduped = new Map();
  merged.forEach(([k, v]) => deduped.set(`${k}:${v}`, [k, v]));

  return Array.from(deduped.values())
    .sort(([ak, av], [bk, bv]) => {
      if (ak === bk) return av.localeCompare(bv);
      return ak.localeCompare(bk);
    })
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
}

function buildCacheKey(url, { params, headers } = {}) {
  const parsed = new URL(url, "http://localhost");
  const query = buildSortedQueryString(url, params);
  const headerIdentity = normalizeHeaderIdentity(headers);
  const originlessUrl = `${parsed.pathname}${query ? `?${query}` : ""}`;
  return `${originlessUrl}|${headerIdentity}`;
}

function resolveDefaultCacheTtlMs(url) {
  const path = new URL(url, "http://localhost").pathname;
  const match = CACHE_TTL_BY_PATH.find(({ matcher }) => matcher.test(path));
  return match?.ttlMs ?? DEFAULT_CACHE_TTL_MS;
}

/**
 * Build standard headers with optional API key and version.
 */
export function buildHeaders(apiKey, version = DEFAULT_API_CONFIG.version) {
  const token = apiKey || getAuthToken();
  return {
    ...DEFAULT_API_CONFIG.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    Version: version,
  };
}

/**
 * Parse/return response or throw on non-OK.
 * Suppresses console logs for statuses listed in SUPPRESS_STATUSES.
 */
async function handle(res) {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const status = res.status;

    if (!SUPPRESS_STATUSES.has(status)) {
      logger.warn("HTTP non-OK response", {
        url: res.url,
        status,
        contentType: res.headers.get("content-type") || "",
        preview: text?.slice(0, 400) || "",
      });
    }

    const err = new Error(`HTTP ${status} ${text || res.statusText}`.trim());
    err.status = status;
    err.body = text;
    throw err;
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

/**
 * Core fetch with retries + timeout per attempt.
 * - Retries on 429 and 5xx (and network errors)
 * - Exponential backoff with jitter (via shared withRetry)
 */
async function fetchWithRetry(finalUrl, init, {
  attempts = DEFAULT_ATTEMPTS,
  baseDelay = DEFAULT_BASE_DELAY,
  timeoutMs = DEFAULT_TIMEOUT_MS,
} = {}) {
  return withRetry(
    async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);

      const res = await fetch(finalUrl, { ...init, signal: controller.signal });
      clearTimeout(timer);

      // Turn retryable HTTP statuses into errors so withRetry can handle them
      if (res.status === 429 || (res.status >= 500 && res.status < 600)) {
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        throw err;
      }
      return res;
    },
    {
      retries: attempts - 1, // withRetry uses retries (not total attempts)
      baseDelayMs: baseDelay,
      retryOn: (err) => {
        const status = err?.status;
        if (!status) return true; // network/timeout/CORS
        return status === 429 || (status >= 500 && status < 600);
      },
    }
  );
}

// ---- Global concurrency limiter ----
// Prevents flooding the GHL API when many enrichment/status calls fire at once.
const MAX_CONCURRENT_REQUESTS = 12;
let activeRequests = 0;
const requestQueue = [];

function acquireSlot() {
  if (activeRequests < MAX_CONCURRENT_REQUESTS) {
    activeRequests++;
    return Promise.resolve();
  }
  return new Promise((resolve) => requestQueue.push(resolve));
}

function releaseSlot() {
  if (requestQueue.length > 0) {
    const next = requestQueue.shift();
    next(); // hand the slot to next waiter (activeRequests stays the same)
  } else {
    activeRequests--;
  }
}

/**
 * Unified request wrapper:
 * - Global concurrency limit (max 8 in-flight requests to GHL)
 * - Builds final URL with query
 * - Retries transient failures
 * - Per-attempt timeout using AbortController
 * - Logs network/throw cases via logger.warn (suppressed per SUPPRESS_STATUSES)
 */
async function request(method, url, body, { params, headers } = {}) {
  const finalUrl = withQuery(url, params);

  await acquireSlot();
  try {
    const init = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);

    const res = await fetchWithRetry(finalUrl, init);
    return await handle(res);
  } catch (e) {
    // Network error, timeout, CORS, or thrown from handle()
    const status = e?.status || 0;
    if (!SUPPRESS_STATUSES.has(status)) {
      logger.warn("HTTP request failed", {
        method,
        url: finalUrl,
        status,
        message: e?.message,
      });
    }
    throw e;
  } finally {
    releaseSlot();
  }
}

/** GET */
export function get(url, { params, headers } = {}) {
  return request("GET", url, undefined, { params, headers });
}

/** Cached GET with in-flight de-duping and TTL-based response cache. */
export function getCached(url, { params, headers, cacheTtlMs } = {}) {
  const ttlMs = Number.isFinite(cacheTtlMs) ? cacheTtlMs : resolveDefaultCacheTtlMs(url);
  const key = buildCacheKey(url, { params, headers });
  const now = Date.now();

  const existing = responseCache.get(key);
  if (existing && existing.expiresAt > now) {
    return Promise.resolve(existing.data);
  }

  if (existing && existing.expiresAt <= now) {
    responseCache.delete(key);
  }

  const pending = inFlight.get(key);
  if (pending) return pending;

  const promise = get(url, { params, headers })
    .then((data) => {
      responseCache.set(key, {
        data,
        expiresAt: Date.now() + Math.max(0, ttlMs),
      });
      return data;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, promise);
  return promise;
}

/** POST (JSON) */
export function post(url, body, { params, headers } = {}) {
  return request("POST", url, body, { params, headers });
}

/** PUT (JSON) */
export function put(url, body, { params, headers } = {}) {
  return request("PUT", url, body, { params, headers });
}

/** DELETE */
export function del(url, { params, headers } = {}) {
  return request("DELETE", url, undefined, { params, headers });
}
